import React, { useState, useEffect, useRef } from "react";
import { Stack, TextField } from "@fluentui/react";
import SendOutlined from "@mui/icons-material/SendOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faPaperclip, faMicrophone, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import Send from "../../assets/Send.svg";
import styles from "./QuestionInput.module.css";
import DocumentUploadDialog from "../DocumentUploadDialog/DocumentUploadDialog";
import { FileStatus } from "../DocumentUploadDialog/DocumentUploadDialog";
import DocumentLoadingIcon from "../HeaderButtons/DocumentLoadingIcon";
import { UserUploadConfig } from "../../pages/chat/types";
import { ChatMessage } from "../../api";

interface Props {
  onSend: (question: string) => void;
  onMicrophoneClick: () => void;
  isListening: boolean;
  isSilenceWarning?: boolean;
  micError?: string;
  micDevices: MediaDeviceInfo[];
  selectedMicDeviceId: string | undefined;
  onMicDeviceChange: (deviceId: string | undefined) => void;
  disabled: boolean;
  isSendButtonDisabled: boolean;
  questionToSend: string;
  setQuestionToSend: (question: string) => void;
  placeholder?: string;
  clearOnSend?: boolean;
  conversationId: string;
  onRefetchConversation?: () => void;
  onFileDeleted?: (uploadedFileName: string) => void;
  resetQuestionToSend: () => void;
  questionToSendId?: string;
  uploadedFiles: FileStatus[];
  userUploadConfig?: UserUploadConfig;
  answers: ChatMessage[];
}

export const QuestionInput = ({
  onSend,
  onMicrophoneClick,
  isListening,
  isSilenceWarning,
  micError,
  micDevices,
  selectedMicDeviceId,
  onMicDeviceChange,
  disabled,
  isSendButtonDisabled,
  questionToSend,
  setQuestionToSend,
  placeholder,
  clearOnSend,
  conversationId,
  onRefetchConversation,
  onFileDeleted,
  resetQuestionToSend,
  questionToSendId,
  uploadedFiles,
  userUploadConfig,
  answers,
}: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMicDropdownOpen, setIsMicDropdownOpen] = useState(false);
  const micDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMicDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (micDropdownRef.current && !micDropdownRef.current.contains(e.target as Node)) {
        setIsMicDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMicDropdownOpen]);
  const [fileStatuses, setFileStatuses] = useState<FileStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setFileStatuses([]);
    setIsUploading(false);
    setIsDialogOpen(false);
  }, [conversationId]);

  useEffect(() => {
    setFileStatuses(uploadedFiles);
  }, [uploadedFiles]);

  const sendQuestion = () => {
    if (disabled || !questionToSend.trim()) {
      return;
    }

    onSend(questionToSend);

    if (clearOnSend) {
      setQuestionToSend("");
    }
  };

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === "Enter" && ev.ctrlKey) {
      ev.preventDefault();
      sendQuestion();
    }
  };

  const onClearQuestion = () => {
    resetQuestionToSend();
  };

  const onQuestionChange = (
    _ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ) => {
    setQuestionToSend(newValue || "");
  };

  const handleUploadProgress = (statuses: FileStatus[], uploading: boolean) => {
    setFileStatuses(statuses);
    setIsUploading(uploading);
  };

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  const uploadedCount = fileStatuses
    ? fileStatuses.filter((status) => status.status === "completed").length
    : 0;
  const totalFiles = fileStatuses ? fileStatuses.length : 0;

  return (
    <Stack horizontal className={styles.questionInputContainer}>
      <TextField
        style={{ backgroundColor: "white" }}
        className={styles.questionInputTextArea}
        placeholder={placeholder}
        multiline
        resizable={false}
        borderless
        value={questionToSend}
        onChange={(e, newValue) => {
          if (newValue !== undefined) {
            onQuestionChange(e, newValue);
          }
        }}
        onKeyDown={onEnterPress}
      />
      <div className={styles.cancelUploadAndSendContainer}>
        <div className={styles.cancelContainer}>
          {(questionToSendId || questionToSend) && (
            <FontAwesomeIcon
              className={styles.cancelIcon}
              icon={faCircleXmark}
              onClick={onClearQuestion}
              role="button"
              style={{ width: "22px", height: "22px" }}
              tabIndex={0}
            />
          )}
        </div>
        <div className={styles.uploadAndSendContainer}>
          <div className={styles.microphoneWrapper} ref={micDropdownRef}>
            {micError && <div className={styles.micError}>{micError}</div>}
            <div
              className={`${styles.questionInputMicrophone} ${!disabled && isListening ? styles.questionInputMicrophoneActive : ""}`}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-label={isListening ? "Stop recording" : "Start recording"}
              aria-disabled={disabled}
              onClick={disabled ? undefined : onMicrophoneClick}
              onKeyDown={(e) => !disabled && (e.key === "Enter" || e.key === " ") && onMicrophoneClick()}
            >
              <FontAwesomeIcon
                icon={faMicrophone}
                style={{
                  width: "18px", height: "22px",
                  color: disabled ? "#BDBDBD" : isListening ? (isSilenceWarning ? "#FF8C00" : "#C50F1F") : "#0F6CBD",
                }}
              />
            </div>
            {micDevices.length > 1 && (
              <div
                className={styles.micChevron}
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setIsMicDropdownOpen(v => !v); }}
              >
                <FontAwesomeIcon icon={faChevronDown} style={{ width: "8px", height: "8px", color: "#0F6CBD" }} />
              </div>
            )}
            {isMicDropdownOpen && (
              <div className={styles.micDropdown}>
                <div
                  className={`${styles.micDropdownItem} ${!selectedMicDeviceId ? styles.micDropdownItemSelected : ""}`}
                  onClick={() => { onMicDeviceChange(undefined); setIsMicDropdownOpen(false); }}
                >
                  Default
                </div>
                {micDevices.map(device => (
                  <div
                    key={device.deviceId}
                    className={`${styles.micDropdownItem} ${selectedMicDeviceId === device.deviceId ? styles.micDropdownItemSelected : ""}`}
                    onClick={() => { onMicDeviceChange(device.deviceId); setIsMicDropdownOpen(false); }}
                  >
                    {device.label || `Microphone ${device.deviceId.slice(0, 6)}`}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className={styles.paperclip}
            onClick={openDialog}
            role="button"
            tabIndex={0}
          >
            {isUploading ? (
              <>
                <DocumentLoadingIcon />
                <span
                  className={styles.uploadCountBubble}
                >{`${uploadedCount}/${totalFiles}`}</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faPaperclip}
                  style={{ width: "22px", height: "22px", verticalAlign: 0 }}
                />
                {uploadedCount > 0 && (
                  <span className={styles.uploadCountBubble}>{totalFiles}</span>
                )}
              </>
            )}
          </div>

          {isSendButtonDisabled ? (
            <SendOutlined className={styles.SendButtonDisabled} />
          ) : (
            <div
              role="button"
              tabIndex={0}
              aria-label="Ask question button"
              onClick={sendQuestion}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " " ? sendQuestion() : null
              }
              className={styles.questionInputSendButtonContainer}
            >
              {disabled ? (
                <SendOutlined
                  className={styles.questionInputSendButtonDisabled}
                />
              ) : (
                <img
                  src={Send}
                  className={styles.questionInputSendButton}
                  alt="Send"
                />
              )}
            </div>
          )}
        </div>

        <DocumentUploadDialog
          isOpen={isDialogOpen}
          onDismiss={closeDialog}
          conversationId={conversationId}
          onUploadProgress={handleUploadProgress}
          onUploadComplete={onRefetchConversation ?? (() => {})}
          onFileDeleted={onFileDeleted}
          uploadedFiles={uploadedFiles}
          userUploadConfig={userUploadConfig}
          answers={answers}
        />
      </div>
    </Stack>
  );
};
